/**
 * Générateur — Attendu 191 — O1 « Il réalise des activités d'algorithmique
 * débranchée. »
 * Niveau : 4ème | Thème #12 Algorithmique (O1) | Session A (Niveaux 1-2).
 * Source prof : « TANOED EXERCISEUR ALGORITHMIQUE ET PROGRAMMATION 4EME »,
 * O1 (Créé n°17 « robot sur quadrillage » + Créé n°18 « algo de calcul en
 * français »). Créé n°18 porte la DOUBLE ÉTIQUETTE O24 (calcul littéral) —
 * décision validée : la carte « pont vers le littéral » vit ici (le thème #3
 * littéral est déjà livré).
 *
 * Convention robot (source de vérité unique, ré-implémentée à l'identique côté
 * solveur) dans `algo.js` : cases (colonne ; ligne), est = +colonne, nord =
 * +ligne, origine (1 ; 1) en bas à gauche, départ orienté EST, « pivote à
 * droite » = quart de tour horaire.
 *
 * ADAPTATIONS scoring (Scratch/tracé non éditables → formats scorables) :
 *  - « exécute à la main » → saisie de la case coloriée (corrigé unique).
 *  - « écris l'algorithme qui colorie X » → QCM : quel programme colorie X ?
 *    (le solveur simule chaque programme candidat).
 *  - « deux algos peuvent-ils colorier la même case ? » → concrétisé : deux
 *    programmes tirés, réponse oui/non genuinely variable (pas de QCM figé).
 *  - la boucle débranchée → QCM d'images (figure coloriée) à `<desc>` relu.
 *
 * 8 cartes — bloc robot (1-4) + bloc calcul en français (5-8) :
 *  1. robot_case   — exécuter un programme → case coloriée (saisie).
 *  2. robot_prog   — quel programme colorie la case X ? (QCM).
 *  3. robot_meme   — deux programmes colorient-ils la même case ? (QCM).
 *  4. robot_boucle — « répète N fois [...] » → quelle figure ? (QCM images).
 *  5. calcul_ordre — remettre les étapes de l'algorithme dans l'ordre (ordonner).
 *  6. calcul_exec  — exécuter l'algorithme pour un nombre donné (saisie).
 *  7. calcul_remarque — que remarque-t-on ? (QCM).
 *  8. calcul_litteral — (a·x + b) ÷ a = x + ? (trou, pont vers O24).
 */

import {
    randInt, randomChoice, shuffle, makeQcm, makeSaisie, makeOrdonner, makeTexteTrou,
} from '../../../utils';
import {
    svgQuadrillage, executerRobot, deplierBoucle, DIRS_ROBOT, imgElement, imgChoix,
} from './algo';

const ATTENDU_ID = 191;
const opts = (label) => ({ niveau: '4eme', matiere: 'maths', attendu: ATTENDU_ID, label });

const L_ROBOT_CASE = 'Le robot : quelle case colorie-t-il ?';
const L_ROBOT_PROG = 'Le robot : quel programme colorie la case ?';
const L_ROBOT_MEME = 'Le robot : la même case ?';
const L_ROBOT_BOUCLE = 'Le robot : la boucle en débranché';
const L_CALCUL_ORDRE = "L'algorithme de calcul : dans l'ordre";
const L_CALCUL_EXEC = "L'algorithme de calcul : exécuter";
const L_CALCUL_REMARQUE = "L'algorithme de calcul : que remarque-t-on ?";
const L_CALCUL_LITTERAL = "L'algorithme de calcul : le prouver avec x";

const INSTRUCTIONS = "<p>Le robot comprend : <b>AVANCE</b> (d'une case), "
    + '<b>PIVOTE À DROITE</b> (quart de tour), <b>PIVOTE À GAUCHE</b>, '
    + '<b>MARQUE</b> (colorie la case). Il part orienté vers l\'est (colonne '
    + 'croissante).</p>';

const seqTxt = (instrs) => instrs.join(', ');

// ═════════ Génération de chemins de robot (en bornes) ═════════

const GRILLE = { cols: 6, rows: 6 };

/** Chemin aléatoire valide (2-3 segments, ≥1 pivot) finissant par MARQUE. */
const tirerChemin = (grille = GRILLE) => {
    for (let t = 0; t < 80; t++) {
        const depart = { c: randInt(1, grille.cols), l: randInt(1, grille.rows) };
        const instrs = [];
        let c = depart.c;
        let l = depart.l;
        let dir = 0;
        let ok = true;
        const nSeg = randInt(2, 3);
        for (let s = 0; s < nSeg && ok; s++) {
            if (s > 0) {
                const turn = randomChoice(['PIVOTE À DROITE', 'PIVOTE À GAUCHE']);
                instrs.push(turn);
                dir = turn.includes('DROITE') ? (dir + 1) % 4 : (dir + 3) % 4;
            }
            const steps = randInt(1, 2);
            for (let a = 0; a < steps; a++) {
                const nc = c + DIRS_ROBOT[dir].dc;
                const nl = l + DIRS_ROBOT[dir].dl;
                if (nc < 1 || nc > grille.cols || nl < 1 || nl > grille.rows) { ok = false; break; }
                c = nc; l = nl; instrs.push('AVANCE');
            }
        }
        if (ok && (c !== depart.c || l !== depart.l)) {
            instrs.push('MARQUE');
            return { depart, instrs, marque: { c, l } };
        }
    }
    return { depart: { c: 1, l: 1 }, instrs: ['AVANCE', 'AVANCE', 'PIVOTE À GAUCHE', 'AVANCE', 'MARQUE'], marque: { c: 3, l: 2 } };
};

/** Programme canonique déplaçant net de (dc, dl) depuis un départ orienté est. */
const programmeVers = (dc, dl, verticalDabord = false) => {
    const horiz = () => {
        const out = [];
        if (dc > 0) { for (let i = 0; i < dc; i++) out.push('AVANCE'); }
        else if (dc < 0) { out.push('PIVOTE À DROITE', 'PIVOTE À DROITE'); for (let i = 0; i < -dc; i++) out.push('AVANCE'); out.push('PIVOTE À GAUCHE', 'PIVOTE À GAUCHE'); }
        return out;
    };
    const vert = () => {
        const out = [];
        if (dl > 0) { out.push('PIVOTE À GAUCHE'); for (let i = 0; i < dl; i++) out.push('AVANCE'); out.push('PIVOTE À DROITE'); }
        else if (dl < 0) { out.push('PIVOTE À DROITE'); for (let i = 0; i < -dl; i++) out.push('AVANCE'); out.push('PIVOTE À GAUCHE'); }
        return out;
    };
    const corps = verticalDabord ? [...vert(), ...horiz()] : [...horiz(), ...vert()];
    return [...corps, 'MARQUE'];
};

// ═════════ Carte 1 — quelle case colorie-t-il ? (saisie) ═════════

const genRobotCase = () => {
    const { depart, instrs, marque } = tirerChemin();
    const svg = svgQuadrillage({ ...GRILLE, depart, orientation: 'est' });
    const texte = INSTRUCTIONS
        + `<p><b>Le robot part de la case (${depart.c} ; ${depart.l}) et exécute :</b></p>`
        + `<p>Programme : ${seqTxt(instrs)}.</p>`
        + '<p>Quelle case colorie-t-il ? Écris ta réponse sous la forme '
        + '<b>colonne;ligne</b> (exemple : 3;2).</p>';
    const payload = makeSaisie(`${marque.c};${marque.l}`, texte,
        { ...opts(L_ROBOT_CASE), elements_additionnels: imgElement(svg) });
    payload._meta.params = { depart, instrs, marque };
    return payload;
};

// ═════════ Carte 2 — quel programme colorie X ? (QCM) ═════════

const genRobotProg = () => {
    const correctChemin = tirerChemin();
    const { depart, marque } = correctChemin;
    const cle = (m) => `${m.c};${m.l}`;
    const vus = new Set([cle(marque)]);
    const distracteurs = [];
    for (let t = 0; t < 120 && distracteurs.length < 3; t++) {
        const alt = tirerChemin();
        // même départ que le correct, case d'arrivée nouvelle
        const instrs = alt.instrs;
        const { marquees } = executerRobot(depart, instrs);
        const fin = marquees[0];
        if (!fin || vus.has(cle(fin))) continue;
        vus.add(cle(fin));
        distracteurs.push(seqTxt(instrs));
    }
    while (distracteurs.length < 3) {   // repli déterministe
        const dc = randInt(-2, 2);
        const dl = randInt(-2, 2);
        if (dc === 0 && dl === 0) continue;
        const nc = depart.c + dc;
        const nl = depart.l + dl;
        if (nc < 1 || nc > GRILLE.cols || nl < 1 || nl > GRILLE.rows || vus.has(`${nc};${nl}`)) continue;
        vus.add(`${nc};${nl}`);
        distracteurs.push(seqTxt(programmeVers(dc, dl)));
    }
    const correct = seqTxt(correctChemin.instrs);
    const svg = svgQuadrillage({ ...GRILLE, depart, orientation: 'est' });
    const texte = INSTRUCTIONS
        + `<p><b>Le robot part de la case (${depart.c} ; ${depart.l}). Quel programme `
        + `colorie la case (${marque.c} ; ${marque.l}) ?</b></p>`;
    const payload = makeQcm([correct, ...distracteurs], correct, texte,
        { ...opts(L_ROBOT_PROG), elements_additionnels: imgElement(svg) });
    payload._meta.params = { depart, marque };
    return payload;
};

// ═════════ Carte 3 — la même case ? (QCM oui/non concret) ═════════

const genRobotMeme = () => {
    const depart = { c: randInt(2, 4), l: randInt(2, 4) };
    const dc = randInt(-1, 2);
    const dl = randInt(-1, 2);
    const memeCase = randomChoice([true, false]);
    const progA = programmeVers(dc || 1, dl, false);
    let progB;
    if (memeCase) {
        progB = programmeVers(dc || 1, dl, true);   // autre route, même arrivée
    } else {
        // arrivée décalée d'une case (garantie ≠)
        const ddc = randomChoice([-1, 1]);
        progB = programmeVers((dc || 1) + ddc, dl, true);
    }
    const finA = executerRobot(depart, progA).marquees[0];
    const finB = executerRobot(depart, progB).marquees[0];
    const vraiMeme = finA.c === finB.c && finA.l === finB.l;
    const correct = vraiMeme ? 'Oui, ils colorient la même case.'
        : 'Non, ils colorient deux cases différentes.';
    const choix = [
        'Oui, ils colorient la même case.',
        'Non, ils colorient deux cases différentes.',
        'On ne peut pas le savoir sans connaître la grille.',
    ];
    const texte = INSTRUCTIONS
        + `<p><b>Le robot part de la case (${depart.c} ; ${depart.l}). Voici deux programmes.</b></p>`
        + `<p>Programme A : ${seqTxt(progA)}.</p>`
        + `<p>Programme B : ${seqTxt(progB)}.</p>`
        + '<p>Colorient-ils la même case ?</p>';
    const payload = makeQcm(choix, correct, texte,
        { ...opts(L_ROBOT_MEME) });
    payload._meta.params = { depart, progA, progB, vraiMeme };
    return payload;
};

// ═════════ Carte 4 — la boucle en débranché (QCM images) ═════════

const GRILLE_BOUCLE = { cols: 7, rows: 7 };

const genRobotBoucle = () => {
    const depart = { c: randInt(2, 4), l: randInt(2, 4) };   // 3×3 positions, motifs en grille 7×7
    const n = randomChoice([3, 4]);
    const sens = randomChoice(['PIVOTE À DROITE', 'PIVOTE À GAUCHE']);
    const corps = ['AVANCE', 'MARQUE', sens];
    const instrs = deplierBoucle(n, corps);
    const { marquees } = executerRobot(depart, instrs);
    const cle = (arr) => arr.map((m) => `${m.c};${m.l}`).sort().join('|');
    const clesVues = new Set([cle(marquees)]);
    const enGrille = (m) => m.every((cel) => cel.c >= 1 && cel.c <= GRILLE_BOUCLE.cols && cel.l >= 1 && cel.l <= GRILLE_BOUCLE.rows);
    // distracteurs : 3 MOTIFS déterministes toujours en grille (départ ∈ [2,3],
    // n ≤ 4) et jamais confondus avec la figure correcte (carré/L ≠ ligne, ≠ diagonale)
    const motifs = [
        Array.from({ length: n }, (_, i) => ({ c: depart.c + i, l: depart.l })),         // ligne horizontale
        Array.from({ length: n }, (_, i) => ({ c: depart.c, l: depart.l + i })),         // ligne verticale
        Array.from({ length: n }, (_, i) => ({ c: depart.c + i, l: depart.l + i })),     // diagonale
    ];
    const candidats = [];
    motifs.forEach((m) => {
        const k = cle(m);
        if (!clesVues.has(k) && enGrille(m) && candidats.length < 3) {
            clesVues.add(k);
            candidats.push(m);
        }
    });
    const imgDe = (m) => imgChoix(svgQuadrillage({
        ...GRILLE_BOUCLE, showDepart: true, depart, orientation: 'est', compact: true, marquees: m,
        desc: m.map((cel) => `${cel.c};${cel.l}`).sort().join('|'),
    }));
    const choixCorrect = imgDe(marquees);
    const choix = [choixCorrect, ...candidats.map(imgDe)];
    const bodyTxt = `AVANCE, MARQUE, ${sens}`;
    const texte = INSTRUCTIONS
        + `<p><b>Le robot part de la case (${depart.c} ; ${depart.l}) et exécute :</b></p>`
        + `<p>répète ${n} fois [ ${bodyTxt} ]</p>`
        + '<p>Quelle figure colorie-t-il ?</p>';
    const payload = makeQcm(choix, choixCorrect, texte,
        { ...opts(L_ROBOT_BOUCLE), elements_additionnels: imgElement(svgQuadrillage({ ...GRILLE_BOUCLE, depart, orientation: 'est' })) });
    payload._meta.params = { depart, n, sens, marquees };
    return payload;
};

// ═════════ Bloc calcul en français (Créé n°18) ═════════
//
// Algorithme : choisir n → ×a → +b (b = a·k) → ÷a. Résultat = n + k (invariant
// exact ∀ n, cœur didactique + pont vers (a·x + b)/a = x + k).

const tirerAlgo = () => {
    const a = randInt(2, 5);
    const k = randInt(2, 6);
    return { a, k, b: a * k };
};

const etapes = ({ a, b }) => [
    'Choisir un nombre.',
    `Multiplier le résultat par ${a}.`,
    `Ajouter ${b} au résultat.`,
    `Diviser le tout par ${a}.`,
];

const enonceAlgo = ({ a, b }) => '<p><b>Voici un algorithme de calcul :</b> on choisit un '
    + `nombre, on le multiplie par ${a}, on ajoute ${b} au résultat, `
    + `puis on divise le tout par ${a}.</p>`;

// ═════════ Carte 5 — dans l'ordre (ordonner) ═════════

const genCalculOrdre = () => {
    const al = tirerAlgo();
    const items = etapes(al);
    const texte = '<p><b>Remets dans l\'ordre les étapes de cet algorithme de calcul.</b></p>'
        + '<p>On choisit un nombre, on le multiplie, on ajoute, puis on divise.</p>';
    const payload = makeOrdonner(items, texte, opts(L_CALCUL_ORDRE));
    payload._meta.params = al;
    return payload;
};

// ═════════ Carte 6 — exécuter (saisie) ═════════

const genCalculExec = () => {
    const al = tirerAlgo();
    const n = randomChoice([randInt(1, 12), -randInt(1, 8)]);
    const res = n + al.k;   // (a·n + b)/a = n + k
    const texte = enonceAlgo(al)
        + `<p><b>Exécute cet algorithme pour le nombre ${n}. Quel résultat obtient-on ?</b></p>`
        + (n < 0 || res < 0 ? '<p>Écris les nombres négatifs avec leur signe (exemple : -3).</p>' : '');
    const payload = makeSaisie(String(res), texte, opts(L_CALCUL_EXEC));
    payload._meta.params = { ...al, n, res };
    return payload;
};

// ═════════ Carte 7 — que remarque-t-on ? (QCM) ═════════

const genCalculRemarque = () => {
    const al = tirerAlgo();
    const correct = `Le résultat est égal au nombre de départ augmenté de ${al.k}.`;
    const choix = [
        correct,
        `Le résultat est égal au nombre de départ multiplié par ${al.a}.`,
        'Le résultat est toujours égal au nombre de départ.',
        `Le résultat est égal au nombre de départ augmenté de ${al.b}.`,
    ];
    const texte = enonceAlgo(al)
        + '<p>On l\'exécute pour plusieurs nombres et on compare le résultat au nombre de départ.</p>'
        + '<p><b>Que remarque-t-on ?</b></p>';
    const payload = makeQcm(choix, correct, texte, opts(L_CALCUL_REMARQUE));
    payload._meta.params = al;
    return payload;
};

// ═════════ Carte 8 — le prouver avec x (trou, pont O24) ═════════

const genCalculLitteral = () => {
    const al = tirerAlgo();
    const phrase = `En choisissant le nombre x, l'algorithme calcule (${al.a}x + ${al.b}) ÷ ${al.a}, `
        + `c'est-à-dire x + {{${al.k}}}.`;
    const texte = enonceAlgo(al)
        + '<p><b>Prouve la remarque avec un nombre quelconque x. Complète :</b></p>';
    const payload = makeTexteTrou(phrase, texte,
        { ...opts(L_CALCUL_LITTERAL), mode: 'saisie' });
    payload._meta.params = al;
    return payload;
};

// ═════════ Assemblage ═════════

const GENERATORS = {
    robot_case_texte_saisie: genRobotCase,
    robot_prog_qcm_unique: genRobotProg,
    robot_meme_qcm_unique: genRobotMeme,
    robot_boucle_qcm_unique: genRobotBoucle,
    calcul_ordre_ordonner: genCalculOrdre,
    calcul_exec_texte_saisie: genCalculExec,
    calcul_remarque_qcm_unique: genCalculRemarque,
    calcul_litteral_texte_trou_saisie: genCalculLitteral,
};

const CARTES = [
    { id: 'robot_case', label: L_ROBOT_CASE },
    { id: 'robot_prog', label: L_ROBOT_PROG },
    { id: 'robot_meme', label: L_ROBOT_MEME },
    { id: 'robot_boucle', label: L_ROBOT_BOUCLE },
    { id: 'calcul_ordre', label: L_CALCUL_ORDRE },
    { id: 'calcul_exec', label: L_CALCUL_EXEC },
    { id: 'calcul_remarque', label: L_CALCUL_REMARQUE },
    { id: 'calcul_litteral', label: L_CALCUL_LITTERAL },
];

export const CATEGORIES = CARTES.map((c) => ({
    id: c.id,
    label: c.label,
    formatsList: Object.keys(GENERATORS).filter((k) => k.startsWith(`${c.id}_`))
        .map((k) => ({ key: k, label: c.label })),
}));

export const generate = (typeExercice) => {
    let key;
    if (typeExercice && GENERATORS[typeExercice]) key = typeExercice;
    else if (typeExercice) {
        const matching = Object.keys(GENERATORS).filter((k) => k.startsWith(typeExercice) || k.includes(typeExercice));
        key = matching.length ? randomChoice(matching) : randomChoice(Object.keys(GENERATORS));
    } else key = randomChoice(Object.keys(GENERATORS));
    const payload = GENERATORS[key]();
    payload._meta.generatorKey = key;
    return payload;
};
